import { useEffect, useRef, type ReactNode } from "react";
import { watchCloseRequest } from "./closeRequest.ts";

export function Dropdown({
  label,
  children,
  className = "",
  closeOnSelect = false,
  menuTitle = "Options",
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
  closeOnSelect?: boolean;
  menuTitle?: string;
}) {
  const ref = useRef<HTMLDetailsElement>(null);
  const close = () => {
    ref.current?.removeAttribute("open");
    ref.current?.querySelector("summary")?.focus();
  };

  // On phones the open menu is a full-screen sheet: Android's back button
  // closes it rather than leaving the page.
  useEffect(() => {
    const details = ref.current;
    if (!details) return;
    let stopWatching: (() => void) | null = null;
    const sync = () => {
      if (details.open && !stopWatching) {
        stopWatching = watchCloseRequest(() => {
          stopWatching = null;
          details.removeAttribute("open");
          details.querySelector("summary")?.focus();
        });
      } else if (!details.open && stopWatching) {
        stopWatching();
        stopWatching = null;
      }
    };
    details.addEventListener("toggle", sync);
    return () => {
      details.removeEventListener("toggle", sync);
      stopWatching?.();
    };
  }, []);

  useEffect(() => {
    let pointerInside = false;
    const closeOutside = (event: Event) => {
      const inside = event.target instanceof Node && Boolean(ref.current?.contains(event.target));
      if (event.type === "pointerdown") pointerInside = inside;
      // WebKit may focus the containing main element while a touch on a
      // button's label is in flight. Closing here removes the button before
      // its click arrives. Keyboard focus leaving the menu still closes it.
      if (event.type === "focusin" && pointerInside) return;
      if (!inside) {
        ref.current?.removeAttribute("open");
      }
    };
    const pointerEnded = () => {
      pointerInside = false;
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && ref.current?.open) {
        ref.current.removeAttribute("open");
        ref.current.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("click", pointerEnded);
    document.addEventListener("pointercancel", pointerEnded);
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("focusin", closeOutside);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("click", pointerEnded);
      document.removeEventListener("pointercancel", pointerEnded);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("focusin", closeOutside);
    };
  }, []);

  return (
    <details ref={ref} className={`dropdown ${className}`}>
      <summary className="dropdown-trigger">
        {label}
        <span className="dropdown-chevron" aria-hidden="true">
          ⌄
        </span>
      </summary>
      <div
        className="dropdown-panel"
        onClick={(event) => {
          if (
            closeOnSelect &&
            event.target instanceof Element &&
            event.target.closest("button, a")
          ) {
            ref.current?.removeAttribute("open");
            ref.current?.querySelector("summary")?.focus();
          }
        }}
      >
        <div className="dropdown-mobile-head">
          <span>{menuTitle}</span>
          <button type="button" className="dropdown-close" onClick={close}>
            Close <span aria-hidden="true">×</span>
          </button>
        </div>
        {children}
      </div>
    </details>
  );
}
